import React from 'react';
import { DriverMap } from '../components/Map/DriverMap';
import { DriverList } from '../components/Drivers/DriverList';
import { Header } from '../components/Layout/Header';
import { Statistics } from '../components/Layout/Statistics';
import { CreateDriverForm } from '../components/Drivers/CreateDriverForm';
import { EditDriverForm } from '../components/Drivers/EditDriverForm';
import { useDrivers } from '../hooks/useDrivers';
import { useLocations } from '../hooks/useLocations';
import { Driver } from '../types/driver.types';
import 'leaflet/dist/leaflet.css';

const Dashboard: React.FC = () => {
  const { drivers, loading: driversLoading, createDriver, updateDriver } = useDrivers();
  const { locations } = useLocations();
  const [showCreateForm, setShowCreateForm] = React.useState(false);
  const [editingDriver, setEditingDriver] = React.useState<Driver | null>(null);
  const [selectedDriver, setSelectedDriver] = React.useState<string | null>(null);
  const [mapCenter, setMapCenter] = React.useState<[number, number]>([6.9271, 79.8612]);

  const handleEditDriver = (driver: Driver) => {
    setEditingDriver(driver);
    setShowCreateForm(false); // Close create form if open
  };

  const handleUpdateDriver = async (data: any) => {
    if (editingDriver) {
      await updateDriver(editingDriver.id, data);
      setEditingDriver(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingDriver(null);
  };

  if (driversLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header onAddDriver={() => {
        setShowCreateForm(!showCreateForm);
        setEditingDriver(null); // Close edit form if open
      }} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {showCreateForm && (
          <CreateDriverForm
            onSubmit={async (data) => {
              await createDriver(data);
              setShowCreateForm(false);
            }}
            onCancel={() => setShowCreateForm(false)}
          />
        )}

        {editingDriver && (
          <EditDriverForm
            driver={editingDriver}
            onSubmit={handleUpdateDriver}
            onCancel={handleCancelEdit}
          />
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <DriverMap
              drivers={drivers}
              locations={locations}
              center={mapCenter}
              selectedDriver={selectedDriver}
              onDriverClick={setSelectedDriver}
            />
          </div>
          
          <div className="space-y-4">
            <DriverList
              drivers={drivers}
              selectedDriver={selectedDriver}
              onDriverSelect={setSelectedDriver}
              onDriverStatusToggle={(id) => updateDriver(id, { active: !drivers.find(d => d.id === id)?.active })}
              onDriverEdit={handleEditDriver}
            />
            <Statistics drivers={drivers} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;